import { Component, HostListener, inject, ElementRef, Input, Output, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { selectCurrentUser } from '../../../store/auth/auth.selectors';
import * as AuthActions from '../../../store/auth/auth.actions';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { ThemeSwitcherComponent } from '../../../shared/components/theme-switcher/theme-switcher.component';
import { NotificationsBellComponent } from '../notifications-bell/notifications-bell.component';
import { FilesService } from '../../../core/services/files.service';
import { Currency, CurrencyService } from '../../../core/services/currency.service';
import { ThemeService } from '../../../core/services/theme.service';

@Component({
    selector: 'app-dashboard-header',
    standalone: true,
    imports: [
        CommonModule,
        RouterLink,
        LucideIconsModule,
        ThemeSwitcherComponent,
        NotificationsBellComponent
    ],
		    templateUrl: './dashboard-header.component.html',
    styleUrls: ['./dashboard-header.component.scss'],
})
export class DashboardHeaderComponent implements OnInit, OnDestroy {
    private store = inject(Store);
    private elementRef = inject(ElementRef);
    private filesService = inject(FilesService);
    private router = inject(Router);
    private themeService = inject(ThemeService);
    currencyService = inject(CurrencyService);
    private destroy$ = new Subject<void>();

    @Input() heading = 'Partner Dashboard';
    @Input() subheading = 'Monitor projects, properties, and activity';
    @Output() toggleSidebar = new EventEmitter<void>();
    user$ = this.store.select(selectCurrentUser);
    open = false;
    openCurrency = false;
    openMobile = false;
    showDisplayCurrencyPrompt = false;
    private readonly displayCurrencyPromptKey = 'nibni_display_currency_prompt_shown';
    currencies: Currency[] = ['USD', 'EUR', 'GBP', 'AED', 'SAR', 'EGP', 'QAR'] as Currency[];
    avatarLoadFailed = false;
    avatarUrl: string | null = null;
    private lastAvatarUserId: string | null = null;
    private lastAvatarFileId: string | null = null;
    private readonly logoBlackSrc = 'https://assetsnibni.zohostratus.com/images/logos/nibni-logo-black.avif';
    private readonly logoWhiteSrc = 'https://assetsnibni.zohostratus.com/images/logos/nibni-logo-white.avif';

    get logoSrc(): string {
        const isDarkTheme = this.themeService.theme() !== 'white';
        return isDarkTheme ? this.logoWhiteSrc : this.logoBlackSrc;
    }

    get homeLink(): string {
        const url = String(this.router.url || '').trim();
        if (url.startsWith('/admin')) return '/admin/dashboard';
        if (url.startsWith('/developer')) return '/developer/dashboard';
        if (url.startsWith('/partner')) return '/developer/dashboard';
        if (url.startsWith('/sales')) return '/developer/dashboard';
        if (url.startsWith('/customer-dashboard')) return '/customer-dashboard/reservations';
        return '/';
    }

    contextBackLink(user: any): { href: string; label: string } | null {
        const url = String(this.router.url || '').trim();
        if (!url.startsWith('/developer')) return null;

        const roleCode = String(user?.role_code || user?.role || '').trim().toLowerCase();
        if (roleCode === 'subpartner' || roleCode === 'sales') return { href: '/developer/dashboard', label: 'Back to Dashboard' };
        return null;
    }

    ngOnInit(): void {
        this.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
            const userId = user?.user_id || user?.id || null;
            if (this.lastAvatarUserId !== (userId || null)) {
                this.lastAvatarUserId = userId || null;
                this.avatarLoadFailed = false;
            }

            const directUrl =
                user?.profile?.PROFILE_PICTURE_URL ||
                user?.profile?.profile_picture_url ||
                user?.profile_picture_url ||
                null;

            const fileId = this.normalizeId(
                user?.profile_picture_file_id ||
                user?.profile?.profile_picture_file_id ||
                user?.profile?.PROFILE_PICTURE_FILE_ID
            );

            if (directUrl && directUrl.trim() !== '') {
                this.setAvatarUrl(directUrl);
                return;
            }

            if (fileId && fileId !== this.lastAvatarFileId && !this.avatarLoadFailed) {
                this.lastAvatarFileId = fileId;
                this.fetchAvatar(fileId);
                return;
            }

            if (!fileId && !directUrl) {
                this.clearAvatarUrl();
            }
        });
    }

    toggleMobile(event: Event): void {
        event.stopPropagation();
        this.openMobile = !this.openMobile;
        if (this.openMobile) {
            this.open = false;
            this.openCurrency = false;
        }
    }

    closeMobile(): void {
        this.openMobile = false;
    }

    toggleDropdown(event: Event) {
        event.stopPropagation();
        this.open = !this.open;
        if (this.open) {
            this.openCurrency = false;
            this.openMobile = false;
        }
    }

    toggleCurrency(event: Event): void {
        event.stopPropagation();
        this.openCurrency = !this.openCurrency;
        if (this.openCurrency) {
            this.open = false;
            this.openMobile = false;
        }
    }

    signOut(): void {
        this.open = false;
        this.openCurrency = false;
        this.openMobile = false;
        this.store.dispatch(AuthActions.signOut({ redirectTo: '/' }));
    }

    selectCurrency(currency: Currency): void {
        const current = this.currencyService.currentCurrency();
        if (current === currency) {
            this.openCurrency = false;
            return;
        }
        this.currencyService.setCurrency(currency);
        this.openCurrency = false;
        this.maybeShowDisplayCurrencyPrompt();
    }

    private maybeShowDisplayCurrencyPrompt(): void {
        try {
            if (localStorage.getItem(this.displayCurrencyPromptKey)) return;
            localStorage.setItem(this.displayCurrencyPromptKey, '1');
        } catch {
            // ignore storage failures
        }
        this.showDisplayCurrencyPrompt = true;
    }

    closeDisplayCurrencyPrompt(): void {
        this.showDisplayCurrencyPrompt = false;
    }

    displayButtonName(user: any): string {
        if (!user) return 'Account';
        const first = String(user?.first_name || user?.firstName || '').trim();
        if (first) return first;
        const full = String(user?.full_name || user?.fullName || '').trim();
        if (full) return full;
        const email = String(user?.email || user?.EMAIL || '').trim();
        return email || 'Account';
    }

    displayFullName(user: any): string {
        if (!user) return 'Account';
        const full = String(user?.full_name || user?.fullName || '').trim();
        if (full) return full;
        const first = String(user?.first_name || user?.firstName || '').trim();
        const last = String(user?.last_name || user?.lastName || '').trim();
        const combined = `${first} ${last}`.trim();
        return combined || this.displayButtonName(user);
    }

    displayOrgName(user: any): string {
        const name = String(user?.company_name || user?.companyName || user?.org_name || user?.orgName || '').trim();
        return name || 'Organization';
    }

    handleAvatarError(): void {
        this.avatarLoadFailed = true;
        this.clearAvatarUrl();
    }

    private fetchAvatar(fileId: string): void {
        this.filesService.downloadPrivateFile(fileId).subscribe({
            next: (blob) => {
                const url = URL.createObjectURL(blob);
                this.setAvatarUrl(url);
            },
            error: () => {
                this.avatarLoadFailed = true;
                this.clearAvatarUrl();
            }
        });
    }

    private setAvatarUrl(url: string): void {
        if (this.avatarUrl && this.avatarUrl.startsWith('blob:')) {
            URL.revokeObjectURL(this.avatarUrl);
        }
        this.avatarUrl = url;
        this.avatarLoadFailed = false;
    }

    private clearAvatarUrl(): void {
        if (this.avatarUrl && this.avatarUrl.startsWith('blob:')) {
            URL.revokeObjectURL(this.avatarUrl);
        }
        this.avatarUrl = null;
    }

    private normalizeId(value: unknown): string | null {
        const raw = String(value || '').trim();
        return raw ? raw : null;
    }

    @HostListener('document:click', ['$event'])
    closeOnOutside(event: Event) {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.open = false;
            this.openCurrency = false;
            this.openMobile = false;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.clearAvatarUrl();
    }

}
